import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const tips = [
  {
    id: 1,
    category: 'Brushing',
    title: 'The Perfect Brushing Technique',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
      </svg>
    ),
    content: [
      'Brush for at least 2 minutes, twice daily',
      'Use a 45-degree angle toward the gumline',
      'Use gentle, circular motions — never scrub',
      'Don\'t forget to brush your tongue',
      'Replace your toothbrush every 3-4 months',
    ],
    color: 'from-[#3AAFA9] to-[#2D8B7A]',
    bgColor: 'bg-emerald-50',
  },
  {
    id: 2,
    category: 'Flossing',
    title: 'Why Flossing Matters',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.848 8.25l1.536.887M7.848 8.25a3 3 0 11-5.196-3 3 3 0 015.196 3zm1.536.887a2.165 2.165 0 011.083 1.839c.005.351.054.695.14 1.024M9.384 9.137l2.077 1.199M7.848 15.75l1.536-.887m-1.536.887a3 3 0 01-5.196 3 3 3 0 015.196-3m1.536.887-2.077 1.199m0-3.328a4.323 4.323 0 002.401.093m0 0c.324.035.643.09.96.166m-9.96-.166a4.322 4.322 0 00-2.401.093m2.401.093l2.077 1.199" />
      </svg>
    ),
    content: [
      'Floss at least once daily — preferably before bed',
      'Use about 18 inches of floss, winding most around your middle finger',
      'Gently curve the floss into a C-shape against each tooth',
      'Slide the floss up and down along the front and back of each tooth',
      'Use a fresh section of floss as you move between teeth',
    ],
    color: 'from-[#E8956A] to-[#D4754B]',
    bgColor: 'bg-orange-50',
  },
  {
    id: 3,
    category: 'Nutrition',
    title: 'Foods That Protect Your Teeth',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8.25v-1.5m0 1.5c-1.355 0-2.697.056-4.024.166C6.845 8.51 6 9.473 6 10.608v2.513m6-4.87c1.355 0 2.697.055 4.024.165C17.155 8.51 18 9.473 18 10.608v2.513m-3-4.87v-1.5m-6 1.5v-1.5m12 9.75l-1.5.75a3.354 3.354 0 11-3 0 3.354 3.354 0 01-3 0l-1.5-.75m4.5 0H12m-4.5-6H12" />
      </svg>
    ),
    content: [
      'Crunchy fruits and vegetables act as natural toothbrushes',
      'Dairy products provide calcium and strengthen enamel',
      'Green and black teas contain compounds that fight bacteria',
      'Nuts provide minerals essential for tooth health',
      'Limit sugary snacks and acidic drinks between meals',
    ],
    color: 'from-[#D4A853] to-[#B8903C]',
    bgColor: 'bg-amber-50',
  },
  {
    id: 4,
    category: 'Kids',
    title: 'Children\'s Dental Health Tips',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.182 15.182a4.5 4.5 0 01-6.364 0M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z" />
      </svg>
    ),
    content: [
      'Start dental visits by age 1 or when first tooth appears',
      'Make brushing fun with colorful toothbrushes and songs',
      'Supervise children\'s brushing until age 7-8',
      'Use a rice-grain-sized amount of fluoride toothpaste for toddlers',
      'Lead by example — brush together as a family!',
    ],
    color: 'from-[#8B5CF6] to-[#7C3AED]',
    bgColor: 'bg-purple-50',
  },
  {
    id: 5,
    category: 'Habits',
    title: 'Habits to Avoid',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
      </svg>
    ),
    content: [
      'Avoid using teeth as tools (opening packages, bottles)',
      'Don\'t chew on ice, hard candy, or pen caps',
      'Limit teeth grinding — ask about a night guard',
      'Avoid excessive snacking throughout the day',
      'Don\'t skip regular dental checkups',
    ],
    color: 'from-[#EF4444] to-[#DC2626]',
    bgColor: 'bg-red-50',
  },
  {
    id: 6,
    category: 'Technology',
    title: 'Modern Dental Technology',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
      </svg>
    ),
    content: [
      'Digital X-rays use 90% less radiation than traditional film',
      'Intraoral cameras let you see what we see in real-time',
      '3D cone beam imaging for precise treatment planning',
      'Laser dentistry for less invasive, faster healing procedures',
      'Same-day crowns with CEREC technology',
    ],
    color: 'from-[#2D8B7A] to-[#3AAFA9]',
    bgColor: 'bg-teal-50',
  },
];

export default function CareTips() {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  return (
    <section id="care-tips" className="section-padding relative">
      <div className="absolute inset-0 mesh-gradient" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)] text-sm font-medium mb-6">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
            </svg>
            Dental Care Tips
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: 'var(--font-display)' }}>
            Expert <span className="gradient-text">Care Tips</span> for a Healthy Smile
          </h2>
          <p className="text-lg text-[var(--color-text-light)] leading-relaxed">
            Dr. Abis-Velasco shares her professional advice for maintaining optimal oral health between visits.
          </p>
        </motion.div>

        {/* Tips Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tips.map((tip, i) => (
            <motion.div
              key={tip.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
            >
              <button
                onClick={() => setExpandedId(expandedId === tip.id ? null : tip.id)}
                className={`w-full text-left bg-white rounded-3xl overflow-hidden transition-all duration-500 ${
                  expandedId === tip.id
                    ? 'shadow-2xl shadow-[var(--color-primary)]/10 ring-2 ring-[var(--color-primary)]/20'
                    : 'shadow-md hover:shadow-xl'
                }`}
              >
                {/* Header */}
                <div className="p-6 pb-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${tip.color} flex items-center justify-center text-white flex-shrink-0 shadow-lg`}>
                      {tip.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-semibold uppercase tracking-wider text-[var(--color-primary)] mb-1 block">{tip.category}</span>
                      <h3 className="text-lg font-bold text-[var(--color-navy)] leading-snug" style={{ fontFamily: 'var(--font-display)' }}>
                        {tip.title}
                      </h3>
                    </div>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${
                      expandedId === tip.id ? 'bg-[var(--color-primary)]/10 rotate-180' : 'bg-gray-50'
                    }`}>
                      <svg className="w-4 h-4 text-[var(--color-text-light)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Expandable content */}
                <AnimatePresence>
                  {expandedId === tip.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className={`px-6 pb-6 pt-2`}>
                        <div className={`${tip.bgColor} rounded-2xl p-5`}>
                          <ul className="space-y-3">
                            {tip.content.map((item, j) => (
                              <motion.li
                                key={j}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: j * 0.05 }}
                                className="flex items-start gap-3"
                              >
                                <span className="w-5 h-5 rounded-full bg-white flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
                                  <svg className="w-3 h-3 text-[var(--color-primary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                  </svg>
                                </span>
                                <span className="text-sm text-[var(--color-text)] leading-relaxed">{item}</span>
                              </motion.li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </button>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-center mt-14"
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
            <div className="text-left">
              <p className="font-semibold text-[var(--color-navy)]" style={{ fontFamily: 'var(--font-display)' }}>Have questions about your dental health?</p>
              <p className="text-sm text-[var(--color-text-light)]">Dr. Abis-Velasco is happy to answer during your visit.</p>
            </div>
            <button
              onClick={() => document.getElementById('appointment')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-6 py-3 bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-primary-light)] text-white font-semibold rounded-xl shadow-lg shadow-[var(--color-primary)]/20 hover:shadow-[var(--color-primary)]/40 hover:scale-105 transition-all whitespace-nowrap"
            >
              Book a Consultation →
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
